
import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// ── Cookie reader utility ─────────────────────────────────────────────────────
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
};

// ── JWT decoder utility ────────────────────────────────────────────────────────
const decodeJWT = (token) => {
    try {
        if (!token) return null;
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error("[Auth] Failed to decode JWT:", e.message);
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let resolvedUser = null;
        let resolvedToken = null;

        // ── METHOD 1: URL params ──────────────────────────────────────────────
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get("token");
        const urlUser = params.get("user");

        if (urlToken && urlUser) {
            try {
                const parsedUser = JSON.parse(decodeURIComponent(urlUser));
                resolvedToken = urlToken;
                resolvedUser = { ...parsedUser, _id: parsedUser._id || parsedUser.id };

                // Immediately enrich role from JWT so it's available before localStorage save
                const urlPayload = decodeJWT(urlToken);
                console.log("[Auth] JWT payload from URL token:", urlPayload);
                if (urlPayload) {
                    const jwtRole = urlPayload.user_role?.role || urlPayload.role || null;
                    console.log("[Auth] JWT role resolved:", jwtRole);
                    if (jwtRole) {
                        resolvedUser.user_role = urlPayload.user_role || { role: jwtRole };
                        resolvedUser.role = jwtRole;
                    }
                }

                localStorage.setItem("auth_token", resolvedToken);
                localStorage.setItem("user", JSON.stringify(resolvedUser));
                window.history.replaceState({}, document.title, window.location.pathname);
                console.log("[Auth] ✓ Session loaded from URL params:", resolvedUser?.name, "role:", resolvedUser?.role);
            } catch (e) {
                console.warn("[Auth] Failed to parse URL params:", e.message);
            }
        }

        // ── METHOD 2: Own localStorage ────────────────────────────────────────
        if (!resolvedUser) {
            const storedUser = localStorage.getItem("user");
            const storedToken = localStorage.getItem("auth_token");
            if (storedUser && storedToken) {
                try {
                    resolvedUser = JSON.parse(storedUser);
                    resolvedToken = storedToken;
                    console.log("[Auth] ✓ Session loaded from own localStorage:", resolvedUser?.name, "role:", resolvedUser?.role, "user_role:", resolvedUser?.user_role);
                } catch (e) {
                    console.warn("[Auth] Failed to parse stored user:", e.message);
                }
            }
        }

        // ── METHOD 3: Cookie fallback ─────────────────────────────────────────
        if (!resolvedUser) {
            const cookieToken = getCookie("auth_token");
            if (cookieToken) {
                resolvedToken = cookieToken;
                localStorage.setItem("auth_token", cookieToken);
                const storedUser = localStorage.getItem("user");
                if (storedUser) {
                    try {
                        resolvedUser = JSON.parse(storedUser);
                        console.log("[Auth] ✓ Session loaded from cookie + stored user:", resolvedUser?.name);
                    } catch (e) { }
                }
            }
        }


        if (resolvedToken) {
            const payload = decodeJWT(resolvedToken);
            if (payload) {
                // Resolve role from JWT — try user_role object first, then flat role field
                const jwtRole = payload.user_role?.role || payload.role || null;

                if (!resolvedUser) {
                    resolvedUser = {
                        _id: payload.user_id,
                        email: payload.email,
                        name: payload.name || payload.username || 'User',
                        user_role: payload.user_role || (jwtRole ? { role: jwtRole } : undefined),
                        role: jwtRole,
                    };
                } else if (jwtRole) {
                    // Always overwrite role from JWT — it is the authoritative source
                    resolvedUser.user_role = payload.user_role || { role: jwtRole };
                    resolvedUser.role = jwtRole;
                    console.log("[Auth] ✓ User role set from JWT:", jwtRole);
                    localStorage.setItem("user", JSON.stringify(resolvedUser));
                }
            }
        }


        // ── Result ────────────────────────────────────────────────────────────
        if (resolvedUser) {
            setUser(resolvedUser);
        } else if (resolvedToken) {
            console.log("[Auth] Token available, user object pending");
        } else {
            console.warn("[Auth] No session found.");
        }

        setLoading(false);
    }, []);

    const login = (userData, token) => {
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
        if (token) localStorage.setItem("auth_token", token);
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem("user");
        localStorage.removeItem("auth_token");
        // Also clear cookie by calling backend logout endpoint
        fetch("http://localhost:1338/api/logout", {
            method: "POST",
            credentials: "include", // sends cookie so backend can clear it
        }).catch(() => { }); // non-fatal
    };

    return (
        <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};